import { FC, useEffect } from 'react'
import { useQuery } from '@apollo/client'

import {
  ArtistsFromCategoriesExtendedQuery,
  ArtistsFromCategoriesExtendedQueryVariables,
} from 'gql'
import { getImageUrl } from 'utils'
import { Card, Typography, Wrapper, Grid } from 'components/ui'
import { STAR } from 'routes/paths'
import Carousel from 'components/carousel'
import { useGeolocation, useScreenClass } from '~hooks'
import { EVENTS_NEAR_YOU_DISTANCE } from 'constants/constant'
import { UserLocationDropdown } from '~shared'

import { STARS_IN_CATEGORY_EXTENDED_QUERY } from 'gql/stars/queries'

type StarsProps = {
  categoryId?: string
  title?: string
  filterItems?: (node: ArtistsFromCategoriesExtendedQuery['allArtists']['edges'][0]['node']) => void
}

const StarsNearYou: FC<StarsProps> = ({ categoryId, title, filterItems = () => true }) => {
  const { coordinates } = useGeolocation()

  const { isMobile } = useScreenClass()

  const {
    data: stars,
    loading,
    fetchMore,
    refetch,
  } = useQuery<ArtistsFromCategoriesExtendedQuery, ArtistsFromCategoriesExtendedQueryVariables>(
    STARS_IN_CATEGORY_EXTENDED_QUERY,
    {
      variables: {
        first: 5,
        status: 'public',
        activeArtworks: true,
        annotateArtworkMinStartDate: true,
        categoryWithChild_Exact: categoryId,
        orderBy: '-featured_date, min_start_date',
        venueCoordinates: coordinates
          ? `${coordinates.latitude},${coordinates.longitude},${EVENTS_NEAR_YOU_DISTANCE}`
          : '',
      },
      notifyOnNetworkStatusChange: true,
      skip: !coordinates,
    }
  )

  const mapDataToObject = ({ node: { fullName, file, slug, id } }) => ({
    link: STAR(slug),
    t: fullName,
    id,
    image: file,
  })

  const items =
    stars?.allArtists.edges.filter(({ node }) => filterItems(node)).map(mapDataToObject) || []

  const pageInfo = stars?.allArtists.pageInfo

  const loadMore = pageInfo?.hasNextPage
    ? () => fetchMore({ variables: { after: pageInfo.endCursor, first: 10 } })
    : null

  useEffect(() => {
    refetch()
  }, [coordinates])

  return (
    <Wrapper>
      <Grid gap={18}>
        <Grid
          gridAutoFlow={isMobile ? 'row' : 'column'}
          justifyContent="start"
          alignItems="center"
          gap={8}
        >
          <Typography.Text color="g-white" size="lg" uppercase>
            {title}
          </Typography.Text>
          <UserLocationDropdown
            locationTitleOptions={{
              size: 'h5',
            }}
          />
        </Grid>
        {(!items || !items.length) && !loading && (
          <Typography.Text color="light-gray">
            No events near this location at this time. Try another location, or browse more events
            below.
          </Typography.Text>
        )}
        <Carousel<(typeof items)[0]>
          dataSource={items}
          renderItem={({ link, t, id, image }) => (
            <Card
              key={id}
              imageSrc={getImageUrl(image, 400, 300)}
              link={link}
              linkTitle="Get tickets"
              title={t}
            />
          )}
          loading={loading}
          fetchMore={loadMore}
        />
      </Grid>
    </Wrapper>
  )
}

export default StarsNearYou
